import React, { useMemo, useState } from 'react';
import ReactECharts from 'components/ThemedECharts';
import DoubleSlider from 'components/DesignSystem/Slider/DoubleSlider';
import useIsMobile from 'utils/hooks/useIsMobile';
import { cssVar } from 'constants/theme';

import { getDataZoomConfig } from './chartConfig';
import { buildTooltipConfig } from './tooltipFormatter';
import './index.scss';

interface ChartComSlidersProps {
  /** Option ECharts pronto vindo do BE (contrato chart-data.json v5/v2). */
  option: Record<string, unknown>;
  /** Classe extra no container externo. */
  className?: string;
  /** Repassado ao ReactECharts. Default true (alinha com uso atual em GraficoSH/SM). */
  notMerge?: boolean;
  /**
   * Rotulo curto acima do titulo no cabecalho mobile (ex.: "WEOL-SH · DIARIO").
   * Ignorado no desktop.
   */
  kicker?: string;
  /**
   * Abre o grafico em tela cheia. Quando definido, o cabecalho mobile ganha o
   * botao de expandir. Ignorado no desktop (e omitido no proprio overlay).
   */
  onExpandir?: () => void;
}

/**
 * Wrapper de chart Windx com sliders V+H.
 *   - Slider V (eixo Y): `dataZoom` nativo do ECharts, styled DS, draggable na lateral direita.
 *   - Slider H (eixo X): `DoubleSlider` DS externo, controla state local
 *     `[zoomStart, zoomEnd]` em % (0..100), injetado no `dataZoom` X (hidden visual).
 *
 * Zero refetch: o slider opera client-side via dataZoom (filterMode: 'none').
 *
 * Padrao replicado de Meteorology/Agro/WaterBalanceCulture/DetailedView/DVCard.
 */
export default function ChartComSliders({
  option,
  className = '',
  notMerge = true,
  kicker,
  onExpandir,
}: ChartComSlidersProps) {
  const [zoom, setZoom] = useState<[number, number]>([0, 100]);
  const isMobile = useIsMobile();

  // O titulo vem no `option` do BE numa linha so e centralizado. Em 390px ele
  // estoura o canvas e fica cortado nos dois lados (visto no gate de print do
  // Windx mobile). O ECharts nao quebra titulo sem uma largura em px, que este
  // componente nao conhece — entao no mobile ele sai do canvas e vira um <p>,
  // onde o CSS quebra sozinho. Desktop nao muda.
  const tituloMobile = isMobile
    ? (option.title as { text?: string } | undefined)?.text
    : undefined;

  // Override de dataZoom: ignora qualquer dataZoom que tenha vindo no option
  // do BE — quem controla zoom agora eh o FE.
  //
  // Override de grid.right: reservar 80px a direita do plot para que o slider
  // V (dataZoom Y, width=8 + right=5%) fique FORA da area do grafico em vez
  // de sobrepor as linhas/colunas mais a direita. Sem este override, o
  // ECharts default coloca o plot ate ~5px da borda direita e o slider Y
  // fica por cima das ultimas series (reportado pela PO em smoke local
  // 2026-05-17).
  //
  // Override de tooltip (bug PO 2026-05-27): com muitas usinas selecionadas
  // (ex.: 15 usinas × 11 modelos = ate 165 series), o tooltip default
  // ultrapassava o viewport e os valores ficavam clipados, alem de mostrar
  // todas as series mesmo as com valor null. `buildTooltipConfig` adiciona
  // formatter que filtra nulos + max-height: 60vh com scroll + enterable +
  // appendToBody (escapa overflow:hidden de wrappers superiores).
  const optionFinal = useMemo(() => {
    const gridOriginal = (option.grid as Record<string, unknown>) || {};
    const tooltipOriginal = option.tooltip as Record<string, unknown> | undefined;
    return {
      ...option,
      ...(tituloMobile ? { title: { ...(option.title as object), show: false } } : {}),
      grid: { ...gridOriginal, right: 80 },
      dataZoom: getDataZoomConfig(zoom[0], zoom[1], 0),
      // Passa as series para o tooltip refletir o lineStyle real (solido, tracejado
      // ou pontilhado) de cada serie no marcador, em vez da bolinha default do ECharts.
      tooltip: buildTooltipConfig(tooltipOriginal, option.series),
    };
  }, [option, zoom, tituloMobile]);

  // No mobile o grafico vira um card com cabecalho proprio (kicker + titulo +
  // expandir), como no prototipo. Antes o titulo era um <p> solto e o botao de
  // tela cheia flutuava acima do grafico, fora de qualquer moldura.
  const comCabecalho = isMobile && Boolean(kicker || tituloMobile || onExpandir);

  return (
    <div
      className={`windx-chart-com-sliders${comCabecalho ? ' windx-chart-com-sliders--card' : ''} ${className}`.trim()}
    >
      {comCabecalho && (
        <div className="windx-chart-com-sliders__cabecalho">
          <div className="windx-chart-com-sliders__cabecalho-textos">
            {kicker && (
              <span className="windx-chart-com-sliders__kicker">{kicker}</span>
            )}
            {tituloMobile && (
              <p className="windx-chart-com-sliders__titulo-mobile">{tituloMobile}</p>
            )}
          </div>
          {onExpandir && (
            <button
              type="button"
              className="windx-chart-com-sliders__expandir"
              aria-label="Ver em tela cheia"
              onClick={onExpandir}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke={cssVar('text-strong')}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            </button>
          )}
        </div>
      )}
      {tituloMobile && !comCabecalho && (
        <p className="windx-chart-com-sliders__titulo-mobile">{tituloMobile}</p>
      )}
      <div className="windx-chart-com-sliders__chart">
        <ReactECharts
          className="h-100"
          option={optionFinal}
          notMerge={notMerge}
        />
      </div>
      <div className="windx-chart-com-sliders__x-slider">
        <DoubleSlider
          min={0}
          max={100}
          value={zoom}
          setValue={(v) => setZoom([v[0], v[1]])}
        />
      </div>
    </div>
  );
}
